/**
 * Pixel bullets: the original's own `BULLETS.BMP` rounds, drawn as
 * camera-facing quads — one per round in flight (user reports, 2026-08-19:
 * "proper gun fire is missing (shooting pixel ammunition)", then "gunfire
 * visuals = streams of pixel projectiles only").
 *
 * There is deliberately **no line geometry** anywhere in the gunfire: a burst
 * reads as a stream of discrete rounds spaced by the rate of fire, and its
 * density and the rounds' flight time are what carry the readability. The
 * previous `LineSegments` streak is gone with the engine's `to` endpoint that
 * fed it.
 *
 * ## What `BULLETS.BMP` actually is
 *
 * Every per-aircraft cockpit `.TTM` loads it, and the ui-reference lane read
 * it as "ammo-belt strip". Rendering all 13 frames says otherwise: frames
 * **0-7 are the rounds in flight** — short dark streaks in graduated sizes
 * (16x4, 16x4, 8x3, 8x3, 8x2, then three 8x5 diagonals) — and frames 8-12 are
 * the *impact* effects (a 24x19 and a 24x21 splash, plus three smaller ones),
 * which the scripts' damage blocks scatter across the cockpit when the player
 * is being hit. The graduated sizes are the giveaway: a 2D game picks a
 * smaller bullet sprite for a more distant round, which is exactly what
 * `bulletFrameFor` reproduces here. That the sheet also carries *diagonal*
 * variants of the same streak is the giveaway for the other half: a streak
 * lies along where the round is going on screen, which is what
 * `bulletStreak` computes.
 *
 * ## Sizing
 *
 * A bullet is drawn at a **constant apparent size**, the way a 2D game's
 * fixed-pixel sprite behaves: its world size scales with distance so it always
 * covers the same fraction of the logical 320x200 frame. That is
 * `three/sprite-screen-size.ts`'s rule — the same one every other sprite in
 * the port is drawn by, `docs/formats/models-tbl.md` §4.3's 1:1 blit, whose
 * worked example in that section is `BULLETS.BMP` itself — rather than a
 * second copy of the arithmetic, which is what lived here until 2026-08-21
 * and which had drifted in two ways: it measured euclidean *range* where a
 * perspective divide wants view-space *depth*, and it left out the VGA
 * 1:1.2 pixel-aspect correction that rule applies to the horizontal axis.
 *
 * ## The quad is never stretched (2026-08-21)
 *
 * A bullet quad's aspect is **always** its frame's own displayed aspect. The
 * previous renderer scaled the long axis alone by the streak's foreshortening
 * and floored it at the short axis ("a streak is never shorter than it is
 * wide"), which meant that a round flying near-enough down the line of sight —
 * i.e. *every* round of a burst fired at something ahead, the overwhelmingly
 * common case — was drawn as a **square** quad with a 16x4 frame crammed into
 * it: a 4:1 horizontal squash, measured at aspect 1.00 against the sheet's
 * 4.00 across every range band of a live census. That is the "much less big …
 * some distorted sprite" of the user's 2026-08-21 report, and both halves of
 * it come from the one bug: the squash also inflates a 4-px-tall streak into a
 * 21.6-px-tall block of ink at 1080p, and a burst's worth of those overlapping
 * at the vanishing point reads as a grey lump rather than a stream.
 *
 * Foreshortening still shortens a streak — that is real, and it is what
 * `bulletStreak` measures — but it is applied as a **uniform** scale over the
 * whole sprite (`bulletStreakScale`), which is the only scaling that cannot
 * distort it. That is also the sheet's own idiom: `BULLETS.BMP` expresses a
 * smaller mark as a smaller *frame*, never as a squashed one, and a uniform
 * shrink is the continuous version of the same ladder.
 *
 * Pure — no three.js — so the pool lifecycle, the range buckets, the streak
 * geometry and the muzzle registration are all unit-testable;
 * `combat-visuals.ts` owns the `InstancedMesh`es that consume the result.
 */

import { MAX_LIVE_ROUNDS, type Vec3 } from "@red-opus/engine";
import type { CockpitPoint } from "@red-opus/baker/schema";
import { metresPerScreenPixel, metresPerScreenPixelX } from "../three/sprite-screen-size.ts";
import { cockpitPrincipalPoint } from "./cockpit-projection.ts";
import type { RoundView } from "./encounter-setup.ts";
import { presentTransform } from "./panel-layout.ts";

/** One pooled bullet, in world space. */
export interface BulletInstance {
  x: number;
  y: number;
  z: number;
  /** World height of the quad — the streak's *short* axis, already distance-compensated. */
  height: number;
  /**
   * World width of the quad — the streak's *long* axis. `width / height` is
   * always the frame's own displayed aspect (its pixel aspect, VGA-corrected);
   * foreshortening shrinks both axes together and never one alone.
   */
  width: number;
  /** Roll of the quad about the camera's view axis, so its long axis lies along the round's apparent travel. */
  angleRad: number;
  /** Which `BULLETS.BMP` frame — an index into `BULLET_FRAMES`. */
  bucket: number;
}

/**
 * The three in-flight frames used, near to far, with their source pixel size.
 * Indices are into `BULLETS.BMP`'s own frame table (see the module doc for
 * why 8-12 are excluded).
 */
export const BULLET_FRAMES: readonly { readonly frame: number; readonly width: number; readonly height: number }[] = [
  { frame: 0, width: 16, height: 4 },
  { frame: 2, width: 8, height: 3 },
  { frame: 4, width: 8, height: 2 },
];

/** Range boundaries between the three frames, metres of **view-space depth** from the camera. */
export const BULLET_NEAR_M = 80;
export const BULLET_MID_M = 250;

/** One quad per round in flight; the engine already caps how many there can be. */
export const MAX_BULLETS = MAX_LIVE_ROUNDS;

export function bulletFrameFor(distanceM: number): number {
  if (distanceM <= BULLET_NEAR_M) return 0;
  if (distanceM <= BULLET_MID_M) return 1;
  return 2;
}

export function createBulletPool(capacity: number = MAX_BULLETS): BulletInstance[] {
  return Array.from({ length: capacity }, () => ({ x: 0, y: 0, z: 0, height: 0, width: 0, angleRad: 0, bucket: 0 }));
}

const NO_SHIFT: Vec3 = { x: 0, y: 0, z: 0 };

function indexInto(length: number, ordinal: number): number {
  return ((Math.trunc(ordinal) % length) + length) % length;
}

/**
 * How far this round has to move to leave the *drawn* gun rather than the
 * sim's own muzzle: where `gun-muzzles.ts` says the muzzle is, less the barrel
 * the engine actually fired it from. Both are world-space deltas from the
 * aircraft's pose origin for the current frame, so the difference is a pure
 * translation of the round's whole path — it leaves the muzzle and flies
 * parallel to the boresight it was fired along.
 *
 * **Zero for every shipped aircraft**, and that is the point: since the
 * `.DAT` gun block was transcribed, the sim spawns its rounds at the airframe
 * muzzle itself, so the two agree and there is nothing to shift. What remains
 * is the fallback for a spec with no `.DAT` mount, where the drawn muzzle
 * comes from the player's cockpit art and only the player's rounds can get
 * one. The round's own ordinal picks both sides of the subtraction — the same
 * counter the engine used — so a burst alternates between barrels and each
 * round keeps the muzzle it started from for its whole flight.
 *
 * Subtracting `barrels` is what stops the two from being added together: the
 * drawn muzzle is an absolute position, not a nudge.
 */
export function muzzleShiftFor(round: RoundView, breeches: readonly Vec3[], barrels: readonly Vec3[]): Vec3 {
  if (!round.fromPlayer || breeches.length === 0) return NO_SHIFT;
  const breech = breeches[indexInto(breeches.length, round.roundIndex)]!;
  if (barrels.length === 0) return breech;
  const barrel = barrels[indexInto(barrels.length, round.roundIndex)]!;
  return { x: breech.x - barrel.x, y: breech.y - barrel.y, z: breech.z - barrel.z };
}

/** The camera basis a streak's apparent bearing is measured in — world-space, orthonormal. */
export interface CameraBasis {
  readonly position: Vec3;
  /** Camera +X in world space (screen right). */
  readonly right: Vec3;
  /** Camera +Y in world space (screen up). */
  readonly up: Vec3;
  /**
   * Camera −Z in world space (where it is looking). The sizing rule divides by
   * **view-space depth**, not by euclidean range — a perspective divide is by
   * `z` — so a round out at the edge of a wide window must not be drawn bigger
   * than one on the boresight at the same depth.
   */
  readonly forward: Vec3;
}

/** How a streak lies on screen: its roll about the view axis, and how much of its length survives foreshortening. */
export interface BulletStreak {
  readonly angleRad: number;
  /**
   * How much of the round's unit direction lands on the screen plane — 1 for
   * one crossing the view square-on, 0 for one flying straight down the line
   * of sight.
   */
  readonly foreshorten: number;
}

/**
 * Where a round's streak points on screen, and how long it reads.
 *
 * A `BULLETS.BMP` in-flight frame is a *streak* — 16 px by 4 — so the axis it
 * is drawn along is the round's own apparent motion, not the screen's
 * horizontal. Composing the quad from the camera's axes alone (what this
 * renderer did until 2026-08-19) lays every round down as a sideways dash
 * whatever it is doing, which is exactly the "aligned sideways" in the report.
 *
 * The apparent direction is the part of the flight direction that is *across*
 * the line of sight — `d − (d·l̂)l̂` — resolved on the camera's right/up axes.
 * Its length is the foreshortening: a round crossing your view is a full-length
 * streak, and one receding down the boresight has no apparent motion at all,
 * so it reads as a point. That is also what makes the degenerate case safe:
 * as the bearing becomes ill-conditioned the quad becomes square, so the angle
 * it is rolled to stops mattering.
 */
export function bulletStreak(position: Vec3, direction: Vec3, camera: CameraBasis): BulletStreak {
  const dx = position.x - camera.position.x;
  const dy = position.y - camera.position.y;
  const dz = position.z - camera.position.z;
  const range = Math.hypot(dx, dy, dz);
  if (range < 1e-6) return { angleRad: 0, foreshorten: 0 };
  const lx = dx / range;
  const ly = dy / range;
  const lz = dz / range;
  const axial = direction.x * lx + direction.y * ly + direction.z * lz;
  const px = direction.x - axial * lx;
  const py = direction.y - axial * ly;
  const pz = direction.z - axial * lz;
  const screenX = px * camera.right.x + py * camera.right.y + pz * camera.right.z;
  const screenY = px * camera.up.x + py * camera.up.y + pz * camera.up.z;
  const foreshorten = Math.hypot(screenX, screenY);
  if (foreshorten < 1e-9) return { angleRad: 0, foreshorten: 0 };
  return { angleRad: Math.atan2(screenY, screenX), foreshorten };
}

/**
 * How much of its frame a foreshortened streak is drawn at — a **uniform**
 * scale over both axes, so the quad keeps the frame's own aspect whatever the
 * round is doing (see the module doc: scaling the long axis alone is what made
 * every forward-fired round a squashed square).
 *
 * The floor is stated in the original's own units: a round is never drawn
 * shorter than **one 200-line pixel tall**, which for a frame `framePx` tall
 * is a scale of `1 / framePx`. That is the same "a rule that deliberately
 * drops perspective needs a floor put back by hand" the 2026-08-21 effects
 * lesson records, applied at the small end instead of the near end. Stating
 * the floor in the frame's *own* height is what makes it rung-independent:
 * whichever frame is picked, a round receding down the boresight is one
 * original pixel tall and its frame's aspect long — 4, 2⅔ and 4 pixels for
 * the three rungs — which is a mark of a few pixels, the scale the report
 * asked for and the scale the sheet's smallest art already is.
 */
export function bulletStreakScale(foreshorten: number, framePx: number): number {
  const floor = 1 / Math.max(framePx, 1);
  return Math.min(1, Math.max(floor, foreshorten));
}

/**
 * Fills `pool` in place from this frame's rounds and returns how many slots
 * are live. The pool's records are mutated, never replaced, so however long a
 * burst runs the quads themselves cost no allocation. Slots beyond the
 * returned count keep their previous contents and must be treated as dead by
 * the caller.
 */
export function fillBulletPool(
  pool: BulletInstance[],
  rounds: readonly RoundView[],
  breeches: readonly Vec3[],
  barrels: readonly Vec3[],
  camera: CameraBasis,
  fovDeg: number,
): number {
  const fovYRadians = (fovDeg * Math.PI) / 180;
  let count = 0;
  for (const round of rounds) {
    if (count >= pool.length) break;
    const shift = muzzleShiftFor(round, breeches, barrels);
    const slot = pool[count]!;
    slot.x = round.position.x + shift.x;
    slot.y = round.position.y + shift.y;
    slot.z = round.position.z + shift.z;
    const depth =
      (slot.x - camera.position.x) * camera.forward.x +
      (slot.y - camera.position.y) * camera.forward.y +
      (slot.z - camera.position.z) * camera.forward.z;
    const bucket = bulletFrameFor(depth);
    const sprite = BULLET_FRAMES[bucket]!;
    const streak = bulletStreak(slot, round.direction, camera);
    const scale = bulletStreakScale(streak.foreshorten, sprite.height);
    slot.bucket = bucket;
    // Both axes take the *same* `scale`, so the quad's aspect is the frame's
    // own displayed aspect and nothing is ever stretched.
    slot.height = sprite.height * scale * metresPerScreenPixel(depth, fovYRadians);
    slot.width = sprite.width * scale * metresPerScreenPixelX(depth, fovYRadians);
    slot.angleRad = streak.angleRad;
    count += 1;
  }
  return count;
}

/** The viewport a cockpit muzzle is registered against — CSS pixels, and the 3D camera's own vertical FOV. */
export interface MuzzleViewport {
  readonly widthPx: number;
  readonly heightPx: number;
  readonly fovDeg: number;
}

/**
 * Turns the cockpit composition's own muzzle-flash positions — logical
 * 320x200 top-left coordinates of the dash sheet's frame 1, which the firing
 * blocks draw at each gun's muzzle — into camera-frame offsets **from the
 * pilot's eye**, so a round is drawn leaving the aeroplane at the pixel the
 * art paints the flash at rather than from the fuselage centreline.
 *
 * **Fallback only since the `.DAT` gun block was transcribed** — see
 * `gun-muzzles.ts`'s header. Every one of the 33 shipped aircraft now carries
 * a real airframe muzzle, so nothing reaches this function in normal play;
 * it serves a spec built without one.
 *
 * ## Why this is a *screen* registration and not an angle
 *
 * The painted panel is not stretched over the 3D viewport: `panel-layout.ts`
 * presents the 320x200 frame at an **integer** nearest-neighbour scale with
 * the VGA 1:1.2 pixel-aspect correction, anchored bottom-centre (AD-009), so
 * at 1280x720 it occupies 640x400 of the window and at 1920x1080 it occupies
 * 1280x1000 — a different place, and a different size, on every viewport. The
 * 3D view meanwhile fills the window at `CAMERA_FOV_DEG`. Since
 * `cockpit-projection.ts` landed they now share an **optical centre** — the
 * camera's axis is sheared onto the painted gunsight — but still not an
 * angular *scale*, so a painted pixel is not a fixed number of degrees and the
 * registration below still has to be redone on every resize.
 *
 * The previous derivation inverted the *3D camera's* projection directly on
 * the logical pixel (`forwardM * tan(fovX/2) * (cx − 160)/160`), which silently
 * assumed they did. It also measured the angle from the aircraft's pose origin
 * while the cockpit camera actually sits at `cockpitEyePosition` — 0.55 m
 * higher — and threw the forward component away even though the engine spawns
 * its rounds at a *different* forward distance (`gunForwardM`, 3.0 m, against
 * the 1.4 m the tangents were computed for). Measured at 1920x1080 that put
 * the drawn origin 203 px below and 41 px right of the painted flash.
 *
 * What is computed instead: put the logical pixel where the panel actually
 * presents it, read that as a viewport NDC, and unproject it through the 3D
 * camera at the depth the sim's own muzzle sits at. The round then leaves the
 * painted breech at *every* window size, which is the property that was asked
 * for. The price is that the breech is a screen registration rather than a
 * fixed point on the airframe: resize the window and the guns move a few
 * centimetres, visible only in an external view and only as a fraction of a
 * degree at chase range. That is the honest cost of a painted panel and a 3D
 * view that do not share a projection.
 *
 * `flashPx` is the flash sprite's size, needed because the composition stores
 * a *top-left* corner.
 */
export function muzzleEyeOffsets(
  muzzles: readonly CockpitPoint[],
  viewport: MuzzleViewport,
  eyeForwardM: number,
  flashPx: { readonly width: number; readonly height: number },
): Vec3[] {
  if (muzzles.length === 0) return [];
  const width = Math.max(viewport.widthPx, 1);
  const height = Math.max(viewport.heightPx, 1);
  const present = presentTransform(width, height);
  const tanY = Math.tan((viewport.fovDeg * Math.PI) / 360);
  const tanX = tanY * (width / height);
  // The axis is on the painted gunsight, not in the middle of the window
  // (`cockpit-projection.ts`), so NDC is measured from *there*. Reading it off
  // the window's centre is what this function did while the camera was still
  // centred, and it would now put every drawn breech 80 px low at 1080p.
  const principal = cockpitPrincipalPoint(width, height);
  return muzzles.map((muzzle) => {
    const canvasX = present.rect.x + (muzzle.x + flashPx.width / 2) * present.scaleX;
    const canvasY = present.rect.y + (muzzle.y + flashPx.height / 2) * present.scaleY;
    const ndcX = (canvasX - principal.x) / (width / 2);
    const ndcY = (principal.y - canvasY) / (height / 2);
    return { x: eyeForwardM * tanX * ndcX, y: eyeForwardM * tanY * ndcY, z: -eyeForwardM };
  });
}

/**
 * The muzzle-flash sprite's size. Both dash sheets in the game carry the
 * same 24x17 frame 1 (`DASH_A.BMP`, `DASH_B.BMP` — pinned by
 * `tools/bake/test/cockpits.test.ts`), and the composition stores the
 * flash's top-left corner, so this is what turns a corner into a centre.
 */
export const MUZZLE_FLASH_SIZE = { width: 24, height: 17 } as const;
